import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import Login from './components/auth/Login';
import Dashboard from './components/dashboard/Dashboard';
import AdminDashboard from './components/admin/AdminDashboard';
import FinanceDashboard from './components/finance/FinanceDashboard';
import AgentDashboard from './components/agent/AgentDashboard';
import Products from './components/products/Products';
import Stock from './components/stock/Stock';
import Transactions from './components/transactions/Transactions';
import Debts from './components/debts/Debts';
import Expenses from './components/expenses/Expenses';
import Reports from './components/reports/Reports';
import Users from './components/users/Users';
import Returns from './components/returns/Returns';
import DepotReturns from './components/returns/DepotReturns';
import Settlements from './components/settlements/Settlements';
import Balances from './components/balances/Balances';
import Layout from './components/layout/Layout';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

// Main App Routes
const AppRoutes = () => {
  const { user } = useAuth();

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" />} />
        <Route path="/login" element={<Navigate to="/dashboard" />} />
        <Route path="/dashboard" element={<Dashboard />} />
        
        {/* Admin Routes */}
        <Route 
          path="/admin/*" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Routes>
                <Route path="/" element={<AdminDashboard />} />
                <Route path="/users" element={<Users />} />
                <Route path="/products" element={<Products />} />
                <Route path="/stock" element={<Stock />} />
                <Route path="/transactions" element={<Transactions />} />
                <Route path="/debts" element={<Debts />} />
                <Route path="/balances" element={<Balances />} />
                <Route path="/returns" element={<Returns />} />
                <Route path="/settlements" element={<Settlements />} />
                <Route path="/expenses" element={<Expenses />} />
                <Route path="/reports" element={<Reports />} />
              </Routes>
            </ProtectedRoute>
          } 
        />
        
        {/* Finance Routes */}
        <Route 
          path="/finance/*" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'finance']}>
              <Routes>
                <Route path="/" element={<FinanceDashboard />} />
                <Route path="/products" element={<Products />} />
                <Route path="/stock" element={<Stock />} />
                <Route path="/transactions" element={<Transactions />} />
                <Route path="/debts" element={<Debts />} />
                <Route path="/returns" element={<Returns />} />
                <Route path="/settlements" element={<Settlements />} />
                <Route path="/expenses" element={<Expenses />} />
                <Route path="/reports" element={<Reports />} />
              </Routes>
            </ProtectedRoute>
          } 
        />
        
        {/* Agent Routes */}
        <Route 
          path="/agent/*" 
          element={
            <ProtectedRoute allowedRoles={['agent', 'depot_sales']}>
              <Routes>
                <Route path="/" element={<AgentDashboard />} />
                <Route path="/stock" element={<Stock />} />
                <Route path="/transactions" element={<Transactions />} />
                <Route path="/debts" element={<Debts />} />
                <Route path="/returns" element={<Returns />} />
                <Route path="/depot-returns" element={<DepotReturns />} />
                <Route path="/reports" element={<Reports />} />
              </Routes>
            </ProtectedRoute>
          } 
        />

        {/* Depot Sales Routes */}
        <Route 
          path="/depot-sales/*" 
          element={
            <ProtectedRoute allowedRoles={['depot_sales']}>
              <Routes>
                <Route path="/" element={<AgentDashboard />} />
                <Route path="/stock" element={<Stock />} />
                <Route path="/transactions" element={<Transactions />} />
                <Route path="/debts" element={<Debts />} />
                <Route path="/returns" element={<Returns />} />
                <Route path="/depot-returns" element={<DepotReturns />} />
                <Route path="/reports" element={<Reports />} />
              </Routes>
            </ProtectedRoute>
          } 
        />
        
        <Route path="*" element={<Navigate to="/dashboard" />} />
      </Routes>
    </Layout>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;
